import React from 'react';
import { Box, Typography, Avatar } from '@mui/material';
import {
  BusinessOutlined as BusinessIcon,
  TrendingUpOutlined as TrendingUpIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface StatCardProps {
  user: any;
  profiles: any[];
  usage: any;
  onContentClick?: () => void;
}

// Business Profiles Card Component
export const BusinessProfilesCard: React.FC<{ user: any; count: number }> = ({ user, count }: { user: any; count: number }) => {
  const { t } = useTranslation('dashboard');
  
  if (user?.role === 'client' || user?.role === 'user') {
    return null;
  }

  return (
    <Box sx={{
      p: 3,
      borderRadius: 2,
      backgroundColor: 'background.paper',
      border: '1px solid',
      borderColor: 'divider',
      height: '100%',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      transition: 'all 0.2s ease',
      '&:hover': {
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        transform: 'translateY(-2px)',
      },
    }}>
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
      }}>
        <Avatar sx={{
          backgroundColor: 'primary.main',
          width: 56,
          height: 56,
        }}>
          <BusinessIcon />
        </Avatar>
        <Box>
          <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main', lineHeight: 1.2 }}>
            {count}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {count === 1 ? t('stats.businessProfile') : t('stats.businessProfiles')}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

// Combined Content Stats Card - Shows both Total and This Month side by side
export const ContentStatsCard: React.FC<{ totalCount: number; monthCount: number; monthLimit: number; onClick?: () => void }> = ({ 
  totalCount, 
  monthCount, 
  monthLimit, 
  onClick 
}: { 
  totalCount: number; 
  monthCount: number; 
  monthLimit: number; 
  onClick?: () => void 
}) => {
  const { t } = useTranslation('dashboard');
  
  return (
    <Box 
      onClick={onClick}
      sx={{
        p: 3,
        borderRadius: 2,
        backgroundColor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        height: '100%',
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        transition: 'all 0.2s ease',
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          transform: 'translateY(-2px)',
          borderColor: onClick ? 'primary.main' : 'divider',
        },
      }}
    >
      <Box sx={{ mb: 2.5 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
          {t('stats.contentGenerated')}
        </Typography>
      </Box>
      
      <Box sx={{ display: 'flex', gap: 3, alignItems: 'center' }}>
        {/* Total Content */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
          <Avatar sx={{
            backgroundColor: 'success.main',
            width: 40,
            height: 40,
          }}>
            <TrendingUpIcon />
          </Avatar>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              {t('stats.total')}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main', lineHeight: 1.2 }}>
              {totalCount}
            </Typography>
          </Box>
        </Box>

        {/* This Month */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
          <Avatar sx={{
            backgroundColor: 'info.main',
            width: 40,
            height: 40,
          }}>
            <CalendarIcon />
          </Avatar>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
              {t('stats.thisMonth')}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 700, color: 'info.main', lineHeight: 1.2 }}>
              {monthCount}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// Legacy component for backward compatibility (returns null now, cards are used directly)
const StatsCards: React.FC<StatCardProps> = () => {
  return null;
};

export default StatsCards;
